// MAP FOES (owner 2026-11-02: 'more enemy variety', approved) — behaviour. Each map has 1–2 foes of its own (stats,
// rosters and tuning: ./mapFoeDefs.ts). They replace a small share of the normal trickle from mid-run (never add to it),
// and every attack is telegraphed by a mark (`marks`: a ground circle or a lane) that lasts exactly as long as the
// wind-up (1 / pace like every foe telegraph); the strike resolves when the mark runs out, and a foe killed during its
// wind-up takes its mark (and the attack) with it.
//
// Ground effects (`zones`: spore clouds, frozen ground, lava splashes) act on the hero AND the foes, like map hazards.
// Hero effects (root, gust push, burning, cooldown jamming) live here; the Sim reads heroMul / heroGrip / cdRate.
//
// A foe with mode < 0 is underground (Loam Delver tunnelling, Dune Lurker buried): left out of the spatial grid, it
// can't be hit, targeted or touch the hero (sim.ts rebuildGrid / damageEnemy), and the codex doesn't count it as seen.
//
// Pure + deterministic: own seeded Rng stream, fixed DT, no three / DOM. The Sim calls: think() per foe from
// updateEnemies, buried() for underground foes, update() once per step, substitute() from the spawner, onKill().
import { Rng } from './util';
import { DT } from './skillStats';
import type { Enemy, Sim } from './sim';
import {
  DELVER, HOPPER, JAMMER, KITE, LASHER, LURKER, MF_BOSS_K, MF_CAP0, MF_CAP_MAX, MF_CAP_STEP, MF_SHARE0, MF_SHARE1, MF_SHARE_RAMP,
  MITE, POD, ROLLER, SKYRAY, SPROUT_D, SPROUT_GAP, SPROUT_MIN, WRAITH, isMapFoe, mfUnit, rosterFor, type MapFoeKind, type RosterEntry,
} from './mapFoeDefs';

/** render / audio cues (never feed back into gameplay) */
export type MapFoeCue = 'dig' | 'surface' | 'erupt' | 'curl' | 'bounce' | 'swell' | 'burst' | 'wither' | 'lashWind' | 'lash' | 'root'
  | 'crouch' | 'leap' | 'land' | 'ignite' | 'gustWind' | 'gust' | 'mark' | 'dive' | 'ambush' | 'lunge' | 'sprout' | 'jam';
export type MapFoeEvent = { t: 'mapFoe'; k: MapFoeCue; kind: MapFoeKind; x: number; z: number; r: number; dx: number; dz: number };

/** A telegraph on the ground: circle (l = 0) of radius r, or a lane: rectangle with half length l along rot (radians
 *  from +x) and half width w, centred on (x, z). t runs from 0 to warn; hurt = the strike deals damage (else it only
 *  pushes / slows). owner = the enemy id whose attack it is. */
export interface FoeMark { id: number; owner: number; kind: MapFoeKind; x: number; z: number; r: number; l: number; w: number; rot: number; t: number; warn: number; hurt: boolean }
/** ZONE kinds: spore cloud (slow), frozen ground (slip), lava splash (burn) */
export const FZ = { spore: 0, ice: 1, lava: 2 } as const;
export type FoeZoneKind = typeof FZ[keyof typeof FZ];
/** A ground patch: forms over `form` s (no effect yet), then acts until t reaches life. */
export interface FoeZone { id: number; kind: FoeZoneKind; x: number; z: number; r: number; t: number; form: number; life: number }
/** most patches alive at once (oldest dropped first) */
export const ZONE_CAP = 48;
/** sim hit colour for lava-splash ticks on foes */
export const LAVA_HIT_COLOR = 0xff6a1a;

export interface MapFoeHost {
  sim: Sim;
  /** a foe hit on the hero (scaled by the foe-damage ramp; i-frames, ward, sidestep apply) */
  hurt(dmg: number): void;
  /** a damage-over-time tick on the hero (burning), godMode-aware */
  dot(hp: number): void;
  /** a lava tick on a foe (no crit) */
  foeDot(e: Enemy, dmg: number): void;
  query(x: number, z: number, r: number, cb: (i: number) => void): void;
  readonly arenaR: number;
}

/** per-foe scratch state (keyed by enemy id) */
interface FoeSt { e: Enemy; id: number; a: number; b: number; x0: number; z0: number; n: number; hit: boolean; mark: number }

const TAU = Math.PI * 2;

export class MapFoeField {
  readonly roster: readonly RosterEntry[];
  readonly firstFrom: number;
  marks: FoeMark[] = [];
  zones: FoeZone[] = [];
  /** hero status (render / HUD read): seconds rooted, seconds burning, seconds of gust push left */
  rootT = 0; burnT = 0; pushT = 0;
  /** skill-cooldown speed this step (1 = normal; JAMMER.rate inside a Hush Beacon aura) */
  cdRate = 1;
  /** hero move multiplier / grip this step (spore clouds, roots / frozen ground) */
  heroMul = 1; heroGrip = 1;
  private rootImmune = 0; private burnDps = 0; private pushX = 0; private pushZ = 0; private pushV = 0; private pushT0 = 1;
  private st = new Map<number, FoeSt>();
  private rng: Rng;
  private nextId = 1;
  private out = { skip: false, tx: 0, tz: 0, spd: 0 };
  private jamWas = false;
  private hasJammer: boolean;

  constructor(private readonly h: MapFoeHost, mapId: string, seed: number) {
    this.roster = rosterFor(mapId);
    this.firstFrom = this.roster.length ? Math.min(...this.roster.map((r) => r.from)) : Infinity;
    this.hasJammer = this.roster.some((r) => r.kind === 'jammer');
    this.rng = new Rng((seed * 7919 + 4243) >>> 0); // own stream: map foes never perturb the shared combat randomness
  }

  get active(): boolean { return this.roster.length > 0; }

  // ------------------------------------------------------------------ spawning
  /** cap on all map foes alive at schedule time t */
  static capAt(t: number, from: number): number { return Math.min(MF_CAP_MAX, MF_CAP0 + Math.floor(Math.max(0, t - from) / MF_CAP_STEP)); }
  /** share of the trickle replaced at schedule time t */
  static shareAt(t: number, from: number): number { return MF_SHARE0 + (MF_SHARE1 - MF_SHARE0) * Math.min(1, Math.max(0, t - from) / MF_SHARE_RAMP); }

  /** Called by the spawner for one regular spawn it is about to place at (sx, sz). Returns -1 to keep the regular spawn,
   *  else this map's foes were placed instead and the value is the extra spawn budget they used (packs). No random draw
   *  happens before the first map foe is due, so the opening of every run is untouched. */
  substitute(t: number, sx: number, sz: number, hpMul: number): number {
    if (t < this.firstFrom) return -1;
    const sim = this.h.sim;
    let total = 0; const per: Record<string, number> = {};
    for (const e of sim.enemies) if (e.alive && e.def.map) { total += mfUnit(e.def.kind); per[e.def.kind] = (per[e.def.kind] ?? 0) + 1; }
    const cap = MapFoeField.capAt(t, this.firstFrom);
    if (total >= cap) return -1;
    const share = MapFoeField.shareAt(t, this.firstFrom) * (sim.boss && sim.boss.alive ? MF_BOSS_K : 1);
    if (this.rng.next() >= share) return -1;
    const open = this.roster.filter((r) => t >= r.from && (per[r.kind] ?? 0) + 1 <= r.cap);
    if (!open.length) return -1;
    let w = 0; for (const r of open) w += r.w;
    let pick = this.rng.next() * w, R = open[0];
    for (const r of open) { pick -= r.w; if (pick <= 0) { R = r; break; } }
    const n = Math.min(R.pack, R.cap - (per[R.kind] ?? 0), Math.floor((cap - total) / mfUnit(R.kind) + 1e-9));
    if (n < 1) return -1;
    return this.place(R, n, sx, sz, hpMul) - 1;
  }

  /** place n foes of a roster entry; returns how many arrived */
  place(R: RosterEntry, n: number, sx: number, sz: number, hpMul: number): number {
    const sim = this.h.sim, p = sim.player;
    let got = 0;
    if (R.arrive === 'edge') {
      for (let k = 0; k < n; k++) {
        const e = sim.spawnEnemy(R.kind, sx + (k ? this.rng.range(-2.2, 2.2) : 0), sz + (k ? this.rng.range(-2.2, 2.2) : 0), false, hpMul);
        if (e) { this.state(e); got++; }
      }
      return got;
    }
    // sprout: ahead of the hero (facing), across the path
    const fa = Math.atan2(p.faceZ, p.faceX) + this.rng.range(-0.7, 0.7), d = this.rng.range(SPROUT_D[0], SPROUT_D[1]);
    const cx = p.x + Math.cos(fa) * d, cz = p.z + Math.sin(fa) * d, px = -Math.sin(fa), pz = Math.cos(fa);
    for (let k = 0; k < n; k++) {
      const o = (k - (n - 1) / 2) * SPROUT_GAP;
      const q = { x: cx + px * o + this.rng.range(-0.8, 0.8), z: cz + pz * o + this.rng.range(-0.8, 0.8) };
      sim.terrain.collide(q, R.kind === 'sporepod' ? 1 : 0.9);
      const lim = this.h.arenaR - 4, dd = Math.hypot(q.x, q.z); if (dd > lim) { q.x *= lim / dd; q.z *= lim / dd; }
      const hx = q.x - p.x, hz = q.z - p.z, hd = Math.hypot(hx, hz);
      if (hd < SPROUT_MIN) { const s = hd > 1e-3 ? SPROUT_MIN / hd : 0; q.x = p.x + (s ? hx * s : SPROUT_MIN); q.z = p.z + hz * s; }
      const e = sim.spawnEnemy(R.kind, q.x, q.z, false, hpMul); if (!e) continue;
      this.state(e); got++;
      if (R.kind === 'lurker') { e.mode = -1; e.modeT = 0; }
      this.cue('sprout', R.kind, e.x, e.z, e.def.radius, 0, 0);
    }
    return got;
  }

  // ------------------------------------------------------------------ per-foe state
  private state(e: Enemy): FoeSt {
    let s = this.st.get(e.id);
    if (s && s.e === e) return s;
    s = { e, id: e.id, a: this.rng.range(0, TAU), b: 0, x0: e.x, z0: e.z, n: 0, hit: false, mark: 0 };
    this.st.set(e.id, s);
    const k = e.def.kind as MapFoeKind;
    if (e.mode >= 0) { e.mode = 0; e.modeT = 0; } // (a foe already underground keeps its state)
    if (k === 'sporepod') { e.modeT = POD.life; e.atkCd = 1.5; } // a fresh sprout can't burst at once
    else e.atkCd = this.rng.range(1.5, 3);
    return s;
  }
  private cue(k: MapFoeCue, kind: MapFoeKind, x: number, z: number, r: number, dx: number, dz: number): void {
    this.h.sim.events.push({ t: 'mapFoe', k, kind, x, z, r, dx, dz });
  }
  private addMark(e: Enemy, s: FoeSt, m: Omit<FoeMark, 'id' | 'owner' | 'kind' | 't'>): FoeMark {
    this.dropMark(s);
    const mk: FoeMark = { id: this.nextId++, owner: e.id, kind: e.def.kind as MapFoeKind, t: 0, ...m };
    this.marks.push(mk); s.mark = mk.id;
    return mk;
  }
  private markOf(s: FoeSt): FoeMark | null { if (!s.mark) return null; for (const m of this.marks) if (m.id === s.mark) return m; return null; }
  private dropMark(s: FoeSt): void { if (!s.mark) return; const i = this.marks.findIndex((m) => m.id === s.mark); if (i >= 0) this.marks.splice(i, 1); s.mark = 0; }
  /** a lane from (x, z) along unit (dx, dz), len long */
  private lane(x: number, z: number, dx: number, dz: number, len: number, hw: number): { x: number; z: number; l: number; w: number; rot: number; r: number } {
    return { x: x + dx * len / 2, z: z + dz * len / 2, l: len / 2, w: hw, rot: Math.atan2(dz, dx), r: 0 };
  }
  /** is (x, z) inside the mark (pad = body margin) */
  static inMark(m: FoeMark, x: number, z: number, pad = 0): boolean {
    const dx = x - m.x, dz = z - m.z;
    if (m.l > 0) { const c = Math.cos(m.rot), s = Math.sin(m.rot); return Math.abs(dx * c + dz * s) <= m.l + pad && Math.abs(-dx * s + dz * c) <= m.w + pad; }
    return dx * dx + dz * dz <= (m.r + pad) * (m.r + pad);
  }
  /** distance to the arena wall from (x, z) along unit (dx, dz) */
  private toWall(x: number, z: number, dx: number, dz: number, r: number): number {
    const R = this.h.arenaR - r, b = x * dx + z * dz, c = x * x + z * z - R * R;
    return Math.max(0, -b + Math.sqrt(Math.max(0, b * b - c)));
  }
  private cd(range: readonly [number, number]): number { return this.rng.range(range[0], range[1]); }

  // ------------------------------------------------------------------ behaviour
  /** A map foe's step (from Sim.updateEnemies, after the shared stun / pin / launch handling). Returns how it moves:
   *  skip = it moved itself (no shared steering / contact this step); else steer along (tx, tz) at spd. */
  think(e: Enemy, nx: number, nz: number, dist: number, spd: number): { skip: boolean; tx: number; tz: number; spd: number } {
    const o = this.out; o.skip = false; o.tx = nx; o.tz = nz; o.spd = spd;
    const s = this.state(e), sim = this.h.sim, p = sim.player, pace = sim.pace, kind = e.def.kind as MapFoeKind;
    const inside = e.edgeT < 0;
    e.atkCd -= DT;
    switch (kind) {
      case 'delver': {
        if (e.mode === 1) { // digging in (visible, rooted): then it vanishes underground
          o.spd = 0; e.modeT -= DT;
          if (e.modeT <= 0) { e.mode = -1; e.modeT = 0; s.n = 0; e.vx = e.vz = 0; this.cue('dig', kind, e.x, e.z, 1.2, 0, 0); }
        } else if (e.mode === 3) { o.spd = 0; e.modeT -= DT; if (e.modeT <= 0) { e.mode = 0; e.atkCd = this.cd(DELVER.cd); } } // exposed
        else if (inside && e.atkCd <= 0 && dist < DELVER.trigger) { e.mode = 1; e.modeT = DELVER.dig / pace; o.spd = 0; this.cue('curl', kind, e.x, e.z, 1.2, 0, 0); }
        break;
      }
      case 'jammer': case 'kite': {
        const keep = kind === 'jammer' ? JAMMER.keep : KITE.keep;
        if (kind === 'kite' && e.mode === 1) { // gust wind-up: hold still, the lane is on the ground
          o.spd = 0; e.modeT -= DT;
          if (e.modeT <= 0) this.gust(e, s);
          break;
        }
        if (kind === 'kite' && e.mode === 3) { e.modeT -= DT; if (e.modeT <= 0) { e.mode = 0; e.atkCd = this.cd(KITE.cd); } }
        // hold station `keep` m from the hero, drifting round him
        s.a += DT * 0.35 * (e.id & 1 ? 1 : -1);
        const ax = e.x - p.x, az = e.z - p.z, ad = Math.hypot(ax, az) || 1;
        const tx = p.x + (ax / ad) * keep + Math.cos(s.a) * 1.5, tz = p.z + (az / ad) * keep + Math.sin(s.a) * 1.5;
        const gx = tx - e.x, gz = tz - e.z, gd = Math.hypot(gx, gz);
        if (gd > 0.05) { o.tx = gx / gd; o.tz = gz / gd; o.spd = Math.min(spd, gd * 2.5); } else o.spd = 0;
        if (kind === 'kite' && inside && e.mode === 0 && e.atkCd <= 0 && dist < keep + 3.5) {
          e.mode = 1; e.modeT = KITE.wind / pace; e.dirX = nx; e.dirZ = nz;
          this.addMark(e, s, { ...this.lane(e.x, e.z, nx, nz, KITE.len, KITE.hw), warn: e.modeT, hurt: false });
          this.cue('gustWind', kind, e.x, e.z, KITE.len, nx, nz);
        }
        break;
      }
      case 'roller': {
        if (e.mode === 1) { // curled, aiming down its lane
          o.spd = 0; e.modeT -= DT;
          if (e.modeT <= 0) { e.mode = 2; s.b = ROLLER.dist; s.n = ROLLER.bounces; s.hit = false; const m = this.markOf(s); if (m) m.t = m.warn; }
          break;
        }
        if (e.mode === 2) { this.roll(e, s); o.skip = true; break; }
        if (e.mode === 3) { o.spd = 0; e.modeT -= DT; if (e.modeT <= 0) { e.mode = 0; e.atkCd = this.cd(ROLLER.cd); } break; } // dizzy
        if (inside && e.atkCd <= 0 && dist < ROLLER.trigger && dist > ROLLER.near) { // (never point-blank: there must be room to read the lane)
          e.mode = 1; e.modeT = ROLLER.curl / pace; e.dirX = nx; e.dirZ = nz; o.spd = 0;
          const L = Math.min(ROLLER.dist, this.toWall(e.x, e.z, nx, nz, e.def.radius));
          this.addMark(e, s, { ...this.lane(e.x, e.z, nx, nz, Math.max(2, L), ROLLER.hw), warn: e.modeT, hurt: true });
          this.cue('curl', kind, e.x, e.z, L, nx, nz);
        }
        break;
      }
      case 'sporepod': {
        o.spd = 0; e.vx = e.vz = 0; e.kx *= 0.5; e.kz *= 0.5;
        e.modeT -= DT; // life
        if (e.mode === 1) {
          s.b -= DT;
          if (s.b <= 0) { this.podBurst(e, s, true); o.skip = true; }
        } else if (e.modeT <= 0) { this.dropMark(s); e.alive = false; this.st.delete(e.id); this.cue('wither', kind, e.x, e.z, 1, 0, 0); o.skip = true; }
        else if (inside && e.atkCd <= 0 && dist < POD.trigger) {
          e.mode = 1; s.b = POD.swell / pace;
          this.addMark(e, s, { x: e.x, z: e.z, r: POD.r, l: 0, w: 0, rot: 0, warn: s.b, hurt: true });
          this.cue('swell', kind, e.x, e.z, POD.r, 0, 0);
        }
        if (!o.skip) o.skip = true; // rooted: no steering, no contact damage (the burst is its only attack)
        break;
      }
      case 'lasher': {
        if (e.mode === 1) {
          o.spd = 0; e.modeT -= DT;
          if (e.modeT <= 0) {
            const m = this.markOf(s);
            if (m && FoeMarkHit(m, p.x, p.z)) { // caught in the lane: hit + rooted (unless just freed from a root)
              this.h.hurt(LASHER.dmg);
              if (this.rootImmune <= 0) { this.rootT = LASHER.root; this.rootImmune = LASHER.root + LASHER.immune; this.cue('root', kind, p.x, p.z, 1, e.dirX, e.dirZ); }
            }
            this.cue('lash', kind, e.x, e.z, LASHER.len, e.dirX, e.dirZ);
            this.dropMark(s); e.mode = 3; e.modeT = LASHER.recover;
          }
        } else if (e.mode === 3) { o.spd *= 0.3; e.modeT -= DT; if (e.modeT <= 0) { e.mode = 0; e.atkCd = this.cd(LASHER.cd); } }
        else if (dist < LASHER.stand) o.spd = 0; // a whip fighter: it stands off at whip range rather than walking into you
        if (e.mode === 0 && inside && e.atkCd <= 0 && dist < LASHER.trigger) {
          e.mode = 1; e.modeT = LASHER.wind / pace; e.dirX = nx; e.dirZ = nz; o.spd = 0;
          this.addMark(e, s, { ...this.lane(e.x, e.z, nx, nz, LASHER.len, LASHER.hw), warn: e.modeT, hurt: true });
          this.cue('lashWind', kind, e.x, e.z, LASHER.len, nx, nz);
        }
        break;
      }
      case 'wraith': {
        // spiral round the hero: orbit radius shrinks from orbit[0] to orbit[1] every `spiral` s
        s.a += DT * (spd / Math.max(2, Math.hypot(e.x - p.x, e.z - p.z))) * (e.id & 1 ? 1 : -1);
        const k = ((sim.time + e.id * 0.37) % WRAITH.spiral) / WRAITH.spiral, R = WRAITH.orbit[0] + (WRAITH.orbit[1] - WRAITH.orbit[0]) * k;
        const gx = p.x + Math.cos(s.a) * R - e.x, gz = p.z + Math.sin(s.a) * R - e.z, gd = Math.hypot(gx, gz) || 1;
        o.tx = gx / gd; o.tz = gz / gd;
        if (inside) {
          e.modeT -= DT;
          if (e.modeT <= 0) { e.modeT = WRAITH.every; this.addZone(FZ.ice, e.x, e.z, WRAITH.r, WRAITH.form, WRAITH.life); }
        }
        break;
      }
      case 'hopper': {
        if (e.mode === 1) { o.spd = 0; e.modeT -= DT; if (e.modeT <= 0) { e.mode = 2; e.modeT = HOPPER.leap / pace; s.b = e.modeT; s.x0 = e.x; s.z0 = e.z; this.cue('leap', kind, e.x, e.z, 1, e.dirX, e.dirZ); } break; }
        if (e.mode === 2) { // in the air: straight to the mark, landing exactly as the warning runs out
          o.skip = true; e.modeT -= DT;
          const m = this.markOf(s), k = 1 - Math.max(0, e.modeT) / s.b;
          if (m) { e.x = s.x0 + (m.x - s.x0) * k; e.z = s.z0 + (m.z - s.z0) * k; }
          e.vx = e.vz = 0;
          if (e.modeT <= 0) {
            if (m && FoeMarkHit(m, p.x, p.z)) this.h.hurt(HOPPER.dmg);
            this.addZone(FZ.lava, e.x, e.z, HOPPER.lavaR, 0.15, HOPPER.lava);
            this.shove(e.x, e.z, HOPPER.r + 0.8, 7, e.id);
            this.cue('land', kind, e.x, e.z, HOPPER.r, 0, 0);
            this.dropMark(s); e.mode = 3; e.modeT = HOPPER.recover;
          }
          break;
        }
        if (e.mode === 3) { o.spd = 0; e.modeT -= DT; if (e.modeT <= 0) { e.mode = 0; e.atkCd = this.cd(HOPPER.cd); } break; }
        if (dist < HOPPER.stalk) o.spd *= 0.25; // a leaper: it creeps at leaping range instead of walking into you
        if (inside && e.atkCd <= 0 && dist < HOPPER.trigger && dist > HOPPER.near) {
          const L = Math.min(dist, HOPPER.maxLeap), lim = this.h.arenaR - 1.5;
          let tx = e.x + nx * L, tz = e.z + nz * L; const td = Math.hypot(tx, tz); if (td > lim) { tx *= lim / td; tz *= lim / td; }
          e.mode = 1; e.modeT = HOPPER.crouch / pace; e.dirX = nx; e.dirZ = nz; o.spd = 0;
          this.addMark(e, s, { x: tx, z: tz, r: HOPPER.r, l: 0, w: 0, rot: 0, warn: (HOPPER.crouch + HOPPER.leap) / pace, hurt: true });
          this.cue('crouch', kind, tx, tz, HOPPER.r, nx, nz);
        }
        break;
      }
      case 'mite': {
        if (inside && dist < e.def.radius * e.scale + 0.6 && Math.abs(e.y - p.y) < 1.6) {
          const iv = p.iframes > 0;
          this.h.hurt(e.def.damage);
          if (!iv) { this.burnT = Math.max(this.burnT, MITE.dur); this.burnDps = MITE.dps; } // the touch landed: set alight (refreshes, never stacks)
          this.cue('ignite', kind, e.x, e.z, 1, nx, nz);
          e.alive = false; this.st.delete(e.id); o.skip = true; // pops: no reward (like a Cinder Husk)
        }
        break;
      }
      case 'skyray': {
        if (e.mode === 1) { // marked: hangs high, then dives so it lands exactly as the warning runs out
          o.spd = 0; e.modeT -= DT;
          const m = this.markOf(s);
          if (e.modeT <= SKYRAY_DIVE / pace) { e.mode = 2; s.x0 = e.x; s.z0 = e.z; s.b = Math.max(DT, e.modeT); this.cue('dive', kind, e.x, e.z, 1, m ? m.x - e.x : 0, m ? m.z - e.z : 0); }
          break;
        }
        if (e.mode === 2) {
          o.skip = true; e.modeT -= DT; e.vx = e.vz = 0;
          const m = this.markOf(s), k = 1 - Math.max(0, e.modeT) / s.b;
          if (m) { e.x = s.x0 + (m.x - s.x0) * k; e.z = s.z0 + (m.z - s.z0) * k; }
          if (e.modeT <= 0) {
            if (m && FoeMarkHit(m, p.x, p.z)) this.h.hurt(SKYRAY.dmg);
            this.shove(e.x, e.z, SKYRAY.r + 1, 9, e.id);
            this.cue('land', kind, e.x, e.z, SKYRAY.r, 0, 0);
            this.dropMark(s); e.mode = 3; e.modeT = SKYRAY.beached;
          }
          break;
        }
        if (e.mode === 3) { o.spd = 0; e.modeT -= DT; if (e.modeT <= 0) { e.mode = 0; e.atkCd = this.cd(SKYRAY.cd); } break; }
        // glide round the hero at ~9 m
        s.a += DT * 0.5 * (e.id & 1 ? 1 : -1);
        const gx = p.x + Math.cos(s.a) * 9 - e.x, gz = p.z + Math.sin(s.a) * 9 - e.z, gd = Math.hypot(gx, gz) || 1;
        o.tx = gx / gd; o.tz = gz / gd;
        if (inside && e.atkCd <= 0 && dist < SKYRAY.trigger) {
          e.mode = 1; e.modeT = SKYRAY.warn / pace; o.spd = 0;
          const lim = this.h.arenaR - 1.5, d = Math.hypot(p.x, p.z), k = d > lim ? lim / d : 1;
          this.addMark(e, s, { x: p.x * k, z: p.z * k, r: SKYRAY.r, l: 0, w: 0, rot: 0, warn: e.modeT, hurt: true });
          this.cue('mark', kind, p.x * k, p.z * k, SKYRAY.r, 0, 0);
        }
        break;
      }
      case 'lurker': {
        if (e.mode === 1) { o.spd = 0; e.modeT -= DT; if (e.modeT <= 0) this.lunge(e, s); break; }
        if (e.mode === 2) { this.lungeStep(e, s); o.skip = true; break; }
        if (inside && e.atkCd <= 0 && dist < LURKER.trigger) {
          e.mode = 1; e.modeT = LURKER.wind / pace; e.dirX = nx; e.dirZ = nz; o.spd = 0;
          this.addMark(e, s, { ...this.lane(e.x, e.z, nx, nz, LURKER.len, LURKER.hw), warn: e.modeT, hurt: true });
          this.cue('lashWind', kind, e.x, e.z, LURKER.len, nx, nz);
        }
        break;
      }
    }
    return o;
  }

  /** an underground foe's step (mode < 0): no contact, no hits; moves itself */
  buried(e: Enemy, dist: number): void {
    const s = this.state(e), sim = this.h.sim, p = sim.player, pace = sim.pace;
    e.vx = e.vz = 0; e.kx = e.kz = 0; e.lift = 0; e.ky = 0; e.stunT = 0; e.pinT = 0;
    const dx = p.x - e.x, dz = p.z - e.z, nx = dist > 1e-3 ? dx / dist : 0, nz = dist > 1e-3 ? dz / dist : 1;
    if (e.def.kind === 'delver') {
      if (e.mode === -1) { // tunnelling after the hero
        s.n += DT;
        const v = DELVER.tunnel * pace * DT;
        if (dist > 0.4) { const st = Math.min(v, dist - 0.3); e.x += nx * st; e.z += nz * st; this.clampIn(e); }
        if (dist < DELVER.lock || s.n >= DELVER.tunnelMax) { // under the hero (or out of breath): the ground starts to bulge
          e.mode = -2; e.modeT = DELVER.warn / pace;
          this.addMark(e, s, { x: e.x, z: e.z, r: DELVER.r, l: 0, w: 0, rot: 0, warn: e.modeT, hurt: true });
          this.cue('mark', 'delver', e.x, e.z, DELVER.r, 0, 0);
        }
      } else { // -2: eruption warning
        e.modeT -= DT;
        if (e.modeT <= 0) {
          const m = this.markOf(s);
          if (m && FoeMarkHit(m, p.x, p.z)) this.h.hurt(DELVER.dmg);
          this.shove(e.x, e.z, DELVER.r + 0.8, 8, e.id);
          this.cue('erupt', 'delver', e.x, e.z, DELVER.r, 0, 0);
          this.dropMark(s); e.mode = 3; e.modeT = DELVER.exposed;
        }
      }
      return;
    }
    if (e.def.kind === 'lurker') {
      if (e.mode === -1) { // buried: waits for the hero to come close, then creeps
        e.modeT += DT;
        if (e.modeT > LURKER.wait && dist > LURKER.trigger * 0.8) { const st = LURKER.creep * pace * DT; e.x += nx * st; e.z += nz * st; this.clampIn(e); }
        if (dist < LURKER.trigger) {
          e.mode = -2; e.modeT = LURKER.wind / pace; e.dirX = nx; e.dirZ = nz;
          this.addMark(e, s, { ...this.lane(e.x, e.z, nx, nz, LURKER.len, LURKER.hw), warn: e.modeT, hurt: true });
          this.cue('ambush', 'lurker', e.x, e.z, LURKER.len, nx, nz);
        }
      } else { e.modeT -= DT; if (e.modeT <= 0) this.lunge(e, s); }
      return;
    }
    e.mode = 0; // (no other foe hides)
  }

  private clampIn(e: Enemy): void { const lim = this.h.arenaR - 1, d = Math.hypot(e.x, e.z); if (d > lim) { e.x *= lim / d; e.z *= lim / d; } }

  private lunge(e: Enemy, s: FoeSt): void {
    e.mode = 2; s.b = LURKER.len; s.hit = false;
    const m = this.markOf(s); if (m) m.t = m.warn;
    this.cue('lunge', 'lurker', e.x, e.z, LURKER.len, e.dirX, e.dirZ);
  }
  private lungeStep(e: Enemy, s: FoeSt): void {
    const p = this.h.sim.player, v = LURKER.speed * this.h.sim.pace * DT, st = Math.min(v, s.b);
    e.x += e.dirX * st; e.z += e.dirZ * st; s.b -= st; this.clampIn(e);
    e.vx = e.dirX * LURKER.speed * this.h.sim.pace; e.vz = e.dirZ * LURKER.speed * this.h.sim.pace;
    const rr = e.def.radius * e.scale + 0.6;
    if (!s.hit && (p.x - e.x) ** 2 + (p.z - e.z) ** 2 < rr * rr) { s.hit = true; this.h.hurt(LURKER.dmg); }
    if (s.b <= 1e-6) { this.dropMark(s); e.mode = 0; e.vx *= 0.2; e.vz *= 0.2; e.atkCd = this.cd(LURKER.cd); }
  }

  /** Rust Roller rolling: straight, ricochets off the wall and solid props, bowls lesser foes aside, hurts the hero once */
  private roll(e: Enemy, s: FoeSt): void {
    const sim = this.h.sim, p = sim.player, r = e.def.radius * e.scale, v = ROLLER.speed * sim.pace;
    const st = Math.min(v * DT, s.b);
    const x0 = e.x + e.dirX * st, z0 = e.z + e.dirZ * st;
    e.x = x0; e.z = z0; s.b -= st;
    sim.terrain.collide(e, r);
    let bx = e.x - x0, bz = e.z - z0, bounced = false;
    if (bx * bx + bz * bz > 1e-8) { const l = Math.hypot(bx, bz); bx /= l; bz /= l; bounced = true; }
    const d = Math.hypot(e.x, e.z), lim = this.h.arenaR - r;
    if (d > lim) { e.x *= lim / d; e.z *= lim / d; bx = -e.x / lim; bz = -e.z / lim; bounced = true; }
    if (bounced) {
      const dot = e.dirX * bx + e.dirZ * bz;
      if (dot < 0) { e.dirX -= 2 * dot * bx; e.dirZ -= 2 * dot * bz; const l = Math.hypot(e.dirX, e.dirZ) || 1; e.dirX /= l; e.dirZ /= l; }
      s.n--; this.cue('bounce', 'roller', e.x, e.z, r, e.dirX, e.dirZ);
    }
    e.vx = e.dirX * v; e.vz = e.dirZ * v;
    const rr = r + 0.55;
    if (!s.hit && (p.x - e.x) ** 2 + (p.z - e.z) ** 2 < rr * rr && Math.abs(e.y - p.y) < 1.6) { s.hit = true; this.h.hurt(ROLLER.dmg); }
    if (sim.tick % 3 === 0) this.shove(e.x, e.z, r + 0.9, 6, e.id, e.dirX, e.dirZ);
    // the lane keeps showing the path ahead (to the wall, or as far as the roll still goes)
    const m = this.markOf(s);
    if (m) { const L = Math.max(1.5, Math.min(s.b, this.toWall(e.x, e.z, e.dirX, e.dirZ, r))); Object.assign(m, this.lane(e.x, e.z, e.dirX, e.dirZ, L, ROLLER.hw)); m.t = m.warn; }
    if (s.b <= 1e-6 || s.n < 0) { this.dropMark(s); e.mode = 3; e.modeT = ROLLER.dizzy; e.vx *= 0.1; e.vz *= 0.1; }
  }

  private gust(e: Enemy, s: FoeSt): void {
    const sim = this.h.sim, p = sim.player, m = this.markOf(s);
    if (m) {
      if (FoeMarkHit(m, p.x, p.z)) { this.pushX = e.dirX; this.pushZ = e.dirZ; this.pushV = KITE.push; this.pushT = this.pushT0 = KITE.pushT; }
      const reach = Math.hypot(m.l, m.w) + 2;
      this.h.query(m.x, m.z, reach, (i) => {
        const o = sim.enemies[i]; if (!o.alive || o === e || o.def.kind === 'boss' || o === sim.boss) return;
        if (MapFoeField.inMark(m, o.x, o.z, o.def.radius * o.scale * 0.5)) sim.impulse(o, e.dirX * 10, e.dirZ * 10, 0);
      });
    }
    this.cue('gust', 'kite', e.x, e.z, KITE.len, e.dirX, e.dirZ);
    this.dropMark(s); e.mode = 3; e.modeT = KITE.recover;
  }

  /** Bloat Pod bursts: spore cloud (slows hero and foes); `armed` = it swelled up next to the hero (hurts, no reward) */
  private podBurst(e: Enemy, s: FoeSt, armed: boolean): void {
    const p = this.h.sim.player, m = this.markOf(s);
    if (armed && m && FoeMarkHit(m, p.x, p.z)) this.h.hurt(POD.dmg);
    this.addZone(FZ.spore, e.x, e.z, POD.r, 0.2, POD.cloud);
    this.cue('burst', 'sporepod', e.x, e.z, POD.r, 0, 0);
    this.dropMark(s);
    if (armed) { e.alive = false; this.st.delete(e.id); }
  }

  /** knock lesser foes away from (x, z) (or along (dx, dz)) */
  private shove(x: number, z: number, r: number, k: number, self: number, dx = 0, dz = 0): void {
    const sim = this.h.sim;
    this.h.query(x, z, r + 1.5, (i) => {
      const o = sim.enemies[i]; if (!o.alive || o.id === self || o.def.kind === 'boss' || o === sim.boss) return;
      let ux = o.x - x, uz = o.z - z; const d = Math.hypot(ux, uz); if (d > r + o.def.radius * o.scale) return;
      if (d > 1e-4) { ux /= d; uz /= d; } else { ux = 1; uz = 0; }
      sim.impulse(o, (ux + dx) * k, (uz + dz) * k, 0);
    });
  }

  private addZone(kind: FoeZoneKind, x: number, z: number, r: number, form: number, life: number): void {
    if (this.zones.length >= ZONE_CAP) this.zones.shift();
    this.zones.push({ id: this.nextId++, kind, x, z, r, t: 0, form, life });
  }

  /** A map foe died (Sim.killEnemy): its attack is cancelled; a Bloat Pod bursts harmlessly into its spore cloud. */
  onKill(e: Enemy): void {
    const s = this.st.get(e.id);
    if (e.def.kind === 'sporepod') { this.addZone(FZ.spore, e.x, e.z, POD.r, 0.2, POD.cloud); this.cue('burst', 'sporepod', e.x, e.z, POD.r, 0, 0); }
    if (s) { this.dropMark(s); this.st.delete(e.id); }
    for (let i = this.marks.length - 1; i >= 0; i--) if (this.marks[i].owner === e.id) this.marks.splice(i, 1);
  }

  // ------------------------------------------------------------------ ground patches
  /** speed multiplier at (x, z) from active spore clouds (1 = none) */
  moveMul(x: number, z: number): number {
    let m = 1;
    for (const zn of this.zones) if (zn.kind === FZ.spore && zn.t >= zn.form && (x - zn.x) ** 2 + (z - zn.z) ** 2 <= zn.r * zn.r) m = Math.min(m, POD.slow);
    return m;
  }
  /** grip lost at (x, z) from active frozen ground (0 .. 1) */
  slip(x: number, z: number): number {
    for (const zn of this.zones) if (zn.kind === FZ.ice && zn.t >= zn.form && (x - zn.x) ** 2 + (z - zn.z) ** 2 <= zn.r * zn.r) return WRAITH.slip;
    return 0;
  }
  private lavaAt(x: number, z: number): boolean {
    for (const zn of this.zones) if (zn.kind === FZ.lava && zn.t >= zn.form && (x - zn.x) ** 2 + (z - zn.z) ** 2 <= zn.r * zn.r) return true;
    return false;
  }

  // ------------------------------------------------------------------ once per step
  /** Once per step after the foes moved: patches and marks age, the hero's status (root, push, burning, jamming). */
  update(): void {
    const sim = this.h.sim, p = sim.player;
    for (let i = this.zones.length - 1; i >= 0; i--) { const z = this.zones[i]; z.t += DT; if (z.t >= z.life) this.zones.splice(i, 1); }
    for (let i = this.marks.length - 1; i >= 0; i--) {
      const m = this.marks[i]; if (m.t < m.warn) m.t = Math.min(m.warn, m.t + DT);
      const s = this.st.get(m.owner); if (!s || !s.e.alive || s.e.id !== m.owner || s.mark !== m.id) this.marks.splice(i, 1); // owner gone: so is its attack
    }
    if (sim.tick % 120 === 0) for (const [id, s] of this.st) if (!s.e.alive || s.e.id !== id) this.st.delete(id);
    // hero status
    this.rootT = Math.max(0, this.rootT - DT); this.rootImmune = Math.max(0, this.rootImmune - DT);
    const zones = this.zones.length > 0;
    this.heroMul = this.rootT > 0 ? 0 : zones ? this.moveMul(p.x, p.z) : 1;
    this.heroGrip = zones ? 1 - 0.9 * this.slip(p.x, p.z) : 1;
    if (this.pushT > 0) {
      const k = this.pushT / this.pushT0, step = this.pushV * k * DT; this.pushT = Math.max(0, this.pushT - DT);
      p.x += this.pushX * step; p.z += this.pushZ * step; sim.terrain.collide(p, 0.6);
      const d = Math.hypot(p.x, p.z), R = this.h.arenaR; if (d > R) { p.x *= R / d; p.z *= R / d; }
    }
    if (this.burnT > 0) { this.burnT = Math.max(0, this.burnT - DT); this.h.dot(this.burnDps * sim.enemyDmgMul(sim.schedTime) * DT); }
    if (zones && this.lavaAt(p.x, p.z)) this.h.dot(HOPPER.burn * DT);
    // lava splashes hurt foes too (twice a second; bosses barely)
    if (zones && sim.tick % 30 === 0) for (const z of this.zones) {
      if (z.kind !== FZ.lava || z.t < z.form) continue;
      this.h.query(z.x, z.z, z.r + 1.5, (i) => {
        const o = sim.enemies[i]; if (!o.alive || (o.x - z.x) ** 2 + (o.z - z.z) ** 2 > z.r * z.r) return;
        this.h.foeDot(o, o.maxHp * 0.015 * HOPPER.burn * 0.5 * (o.def.kind === 'boss' || o === sim.boss ? 0.25 : 1));
      });
    }
    // Hush Beacon auras
    let jam = false;
    if (this.hasJammer) {
      const R2 = JAMMER.r * JAMMER.r;
      for (const e of sim.enemies) if (e.alive && e.def.kind === 'jammer' && e.edgeT < 0 && (e.x - p.x) ** 2 + (e.z - p.z) ** 2 <= R2) { jam = true; break; }
    }
    this.cdRate = jam ? JAMMER.rate : 1;
    if (jam && !this.jamWas) this.cue('jam', 'jammer', p.x, p.z, JAMMER.r, 0, 0);
    this.jamWas = jam;
  }

  // ------------------------------------------------------------------ render helpers (read-only)
  /** 0..1 wind-up of a foe for its pose (render) */
  windK(e: Enemy): number { return e.mode === 1 || e.mode === -2 ? 1 : 0; }
  /** the mark of a foe's current attack (render), or null */
  markFor(e: Enemy): FoeMark | null { const s = this.st.get(e.id); return s && s.e === e ? this.markOf(s) : null; }
  /** the leap / dive progress 0..1 of a hopper / sky ray in mode 2 (render arc) */
  airK(e: Enemy): number { const s = this.st.get(e.id); if (!s || s.e !== e || e.mode !== 2 || !(s.b > 0)) return 0; return Math.min(1, Math.max(0, 1 - e.modeT / s.b)); }
}

/** the last stretch of a Sky Ray's warning spent diving (s, before pace) */
export const SKYRAY_DIVE = 0.4;
/** does a strike from mark m reach the hero at (x, z)? (a small body margin) */
export function FoeMarkHit(m: FoeMark, x: number, z: number): boolean { return MapFoeField.inMark(m, x, z, 0.35); }
export { isMapFoe };
